import {
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

/**
 * Modern Pagination Component
 *
 * Features:
 * - Responsive
 * - Glassmorphism style
 * - Smooth hover animation
 * - Smart ellipsis
 * - Mobile friendly
 */

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}) {
  if (totalPages <= 1) return null;

  const isMobile =
    typeof window !== "undefined" &&
    window.innerWidth < 768;

  const maxVisible = isMobile ? 4 : 10;

  function getPageNumbers() {
    if (totalPages <= maxVisible) {
      return Array.from(
        { length: totalPages },
        (_, i) => i + 1,
      );
    }

    const pages = [];

    const sideCount = Math.floor(
      (maxVisible - 2) / 2,
    );

    pages.push(1);

    const leftBound = Math.max(
      2,
      currentPage - sideCount,
    );

    const rightBound = Math.min(
      totalPages - 1,
      currentPage + sideCount,
    );

    if (leftBound > 2) {
      pages.push("left-ellipsis");
    }

    for (
      let i = leftBound;
      i <= rightBound;
      i++
    ) {
      pages.push(i);
    }

    if (rightBound < totalPages - 1) {
      pages.push("right-ellipsis");
    }

    pages.push(totalPages);

    return pages;
  }

  const pageNumbers = getPageNumbers();

  return (
    <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-4">
      {/* Info */}
      <div className="text-xs md:text-sm text-gray-500">
        Page{" "}
        <span className="font-semibold text-gray-800">
          {currentPage}
        </span>{" "}
        of{" "}
        <span className="font-semibold text-gray-800">
          {totalPages}
        </span>
      </div>

      {/* Pagination */}
      <div className="relative">
        {/* Glow */}
        <div className="absolute inset-0 bg-cyan-200/20 blur-2xl rounded-full pointer-events-none" />

        <div className="relative flex items-center gap-2 rounded-3xl border border-gray-200 bg-white/70 backdrop-blur-xl shadow-lg px-2 py-2">
          {/* Previous */}
          <button
            onClick={() =>
              onPageChange(
                Math.max(1, currentPage - 1),
              )
            }
            disabled={currentPage === 1}
            className="group h-10 px-3 md:px-4 rounded-2xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
          >
            <ChevronLeft
              size={16}
              className="group-hover:-translate-x-0.5 transition-transform"
            />

            <span className="hidden md:block text-sm font-medium">
              Prev
            </span>
          </button>

          {/* Numbers */}
          <div className="flex items-center gap-1">
            {pageNumbers.map((item) => {
              // LEFT DOTS
              if (item === "left-ellipsis") {
                return (
                  <button
                    key="left-dots"
                    onClick={() =>
                      onPageChange(
                        Math.max(
                          1,
                          currentPage - maxVisible,
                        ),
                      )
                    }
                    className="w-10 h-10 rounded-2xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-all duration-300 text-sm shadow-sm"
                  >
                    ...
                  </button>
                );
              }

              // RIGHT DOTS
              if (item === "right-ellipsis") {
                return (
                  <button
                    key="right-dots"
                    onClick={() =>
                      onPageChange(
                        Math.min(
                          totalPages,
                          currentPage +
                            maxVisible,
                        ),
                      )
                    }
                    className="w-10 h-10 rounded-2xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-all duration-300 text-sm shadow-sm"
                  >
                    ...
                  </button>
                );
              }

              // PAGE BUTTON
              const isActive =
                currentPage === item;

              return (
                <button
                  key={item}
                  onClick={() =>
                    onPageChange(item)
                  }
                  className={`relative w-10 h-10 md:w-11 md:h-11 rounded-2xl text-sm font-medium transition-all duration-300 shadow-sm ${
                    isActive
                      ? "bg-cyan-600 text-white shadow-cyan-200 shadow-lg scale-105"
                      : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 hover:-translate-y-0.5"
                  }`}
                >
                  {/* Active Glow */}
                  {isActive && (
                    <div className="absolute inset-0 rounded-2xl bg-cyan-400/20 blur-xl" />
                  )}

                  <span className="relative z-10">
                    {item}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Next */}
          <button
            onClick={() =>
              onPageChange(
                Math.min(
                  totalPages,
                  currentPage + 1,
                ),
              )
            }
            disabled={
              currentPage === totalPages
            }
            className="group h-10 px-3 md:px-4 rounded-2xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
          >
            <span className="hidden md:block text-sm font-medium">
              Next
            </span>

            <ChevronRight
              size={16}
              className="group-hover:translate-x-0.5 transition-transform"
            />
          </button>
        </div>
      </div>
    </div>
  );
}
